/**
 * Sections of the agency salon page, in display order. The active one lives in the URL (`?tab=plan`) so deep links,
 * refresh and the back button keep it; `features` is the default and is never written to the URL.
 */
export const AGENCY_SALON_TABS = ['features', 'access', 'plan', 'tap'] as const;

export type AgencySalonTab = (typeof AGENCY_SALON_TABS)[number];

export const DEFAULT_AGENCY_SALON_TAB = 'features' satisfies AgencySalonTab;

export const AGENCY_SALON_TAB_LABELS: Record<AgencySalonTab, string> = {
  features: 'Features',
  access: 'Menus & access',
  plan: 'Plan & price',
  tap: 'Hayven Tap',
};

/** The stand registry is agency-admin only (the API refuses members), so members never get its tab. */
export function agencySalonTabsFor(isAdmin: boolean): readonly AgencySalonTab[] {
  return isAdmin ? AGENCY_SALON_TABS : AGENCY_SALON_TABS.filter((tab) => tab !== 'tap');
}

/** The tab to show: the asked one when this viewer has it, else the default (a member opening a shared `?tab=tap`). */
export function resolveAgencySalonTab(tab: AgencySalonTab, isAdmin: boolean): AgencySalonTab {
  return agencySalonTabsFor(isAdmin).includes(tab) ? tab : DEFAULT_AGENCY_SALON_TAB;
}

function isAgencySalonTab(value: unknown): value is AgencySalonTab {
  return typeof value === 'string' && (AGENCY_SALON_TABS as readonly string[]).includes(value);
}

export type AgencySalonSearchParams = {
  tab?: AgencySalonTab;
};

/**
 * Search for a "Business settings" link. "N features off" counts hidden menus, and their switches are on Menus &
 * access, so a link that names them lands there; with nothing hidden it opens the default tab.
 */
export function agencySalonSettingsSearch(hasHiddenMenu: boolean): AgencySalonSearchParams {
  return hasHiddenMenu ? { tab: 'access' } : {};
}

/** Route `validateSearch`: keeps a known non-default tab, drops anything else. */
export function parseAgencySalonSearch(search: Record<string, unknown>): AgencySalonSearchParams {
  return isAgencySalonTab(search.tab) && search.tab !== DEFAULT_AGENCY_SALON_TAB ? { tab: search.tab } : {};
}
